import Phaser from "phaser";

import {
  OnboardingGuideScene,
  type OnboardingGuidePresentation,
} from "./OnboardingGuideScene";

export type OnboardingGuideRuntime = {
  game: Phaser.Game;
  scene: OnboardingGuideScene;
  ready: Promise<void>;
  renderScale: number;
  setPresentation(presentation: OnboardingGuidePresentation): void;
  celebrate(): void;
  resize(width: number, height: number): void;
  destroy(): void;
};

const MAX_RENDER_SCALE = 2;

function getRenderScale(): number {
  return Math.min(MAX_RENDER_SCALE, Math.max(1, window.devicePixelRatio || 1));
}

export function createOnboardingGuideGame(
  parent: HTMLElement,
  presentation: OnboardingGuidePresentation,
): OnboardingGuideRuntime {
  const renderScale = getRenderScale();
  const logicalWidth = parent.clientWidth || 164;
  const logicalHeight = parent.clientHeight || 286;
  let resolveReady: () => void = () => undefined;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  const scene = new OnboardingGuideScene({ onReady: resolveReady }, presentation, renderScale);
  const game = new Phaser.Game({
    type: Phaser.CANVAS,
    parent,
    width: Math.round(logicalWidth * renderScale),
    height: Math.round(logicalHeight * renderScale),
    transparent: true,
    backgroundColor: "rgba(0,0,0,0)",
    antialias: true,
    render: { antialias: true, pixelArt: false, roundPixels: false },
    scale: { mode: Phaser.Scale.NONE, autoCenter: Phaser.Scale.NO_CENTER },
    scene: [scene],
    banner: false,
  });
  const canvas = parent.querySelector("canvas");
  if (canvas !== null) {
    canvas.style.pointerEvents = "none";
    canvas.setAttribute("aria-hidden", "true");
  }
  return {
    game,
    scene,
    ready,
    renderScale,
    setPresentation(next): void {
      scene.setPresentation(next);
    },
    celebrate(): void {
      scene.celebrate();
    },
    resize(width, height): void {
      game.scale.resize(Math.round(width * renderScale), Math.round(height * renderScale));
    },
    destroy(): void {
      game.destroy(true);
    },
  };
}
